import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Users } from "lucide-react"
import { useState } from "react"

import { type UserPublic, type UserRole, UsersService } from "@/client"
import AddUser from "@/components/Admin/AddUser"
import { columns, type UserTableData } from "@/components/Admin/columns"
import { DataTable } from "@/components/Common/DataTable"
import PendingUsers from "@/components/Pending/PendingUsers"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import useAuth from "@/hooks/useAuth"

export const Route = createFileRoute("/_layout/admin")({
  component: Admin,
  staticData: {
    pageTitle: "User Management",
    pageSubtitle: "View and manage all users in the system",
    pageIcon: Users,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "User Management - Broken Islands Booking",
      },
    ],
  }),
})

function UsersTableContent({
  roleFilter,
  statusFilter,
}: {
  roleFilter: string
  statusFilter: string
}) {
  const { user: currentUser } = useAuth()
  const { data: users, isLoading } = useQuery({
    queryFn: () =>
      UsersService.readUsers({
        skip: 0,
        limit: 100,
        role: (roleFilter || undefined) as UserRole | undefined,
        isActive: statusFilter === "" ? undefined : statusFilter === "active",
      }),
    queryKey: ["users", roleFilter, statusFilter],
  })

  if (isLoading) return <PendingUsers />
  if (!users) return null

  const tableData: UserTableData[] = users.data.map((user: UserPublic) => ({
    ...user,
    isCurrentUser: currentUser?.id === user.id,
  }))

  return <DataTable columns={columns} data={tableData} />
}

function Admin() {
  const [roleFilter, setRoleFilter] = useState<string>("")
  const [statusFilter, setStatusFilter] = useState<string>("")

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <AddUser />
      </div>
      <div className="flex gap-3">
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="booking_coordinator">
              Booking Coordinator
            </SelectItem>
            <SelectItem value="dock_staff">Dock Staff</SelectItem>
            <SelectItem value="kitchen_staff">Kitchen Staff</SelectItem>
            <SelectItem value="taxi_staff">Taxi Staff</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <UsersTableContent
        roleFilter={roleFilter === "all" ? "" : roleFilter}
        statusFilter={statusFilter === "all" ? "" : statusFilter}
      />
    </div>
  )
}
